// What waits behind each companion tab and More row, counted with the
// shared attention rules and worded like the desktop sidebar's badges
// (features/attention/predicates), so the phone and the dashboard agree.
//
// The phone does NOT mount the whole useAttentionCounts bundle: it never
// shows deployment or handoff counts, and every workshop-proposal fetch
// costs one gateway tool call per agent - over an hour of recording that
// adds up. The shell reads only what its badges need (meetings, todos
// due, the skill review queue, tasks that need you); the More screen adds the workshop
// proposals for its Skills row, and only while it is open. Every hook is
// called with the same arguments as useAttentionCounts and the screens,
// so the caches are shared and nothing polls faster than it did.
import { useMemo } from "react";

import { appsWaiting, useAppsSummary } from "@/api/appsSummary";
import { useCustomerSettings } from "@/api/customerSettings";
import { useMeetings } from "@/api/meetings";
import { usePendingSkills } from "@/api/skills";
import { useWorkshopProposals } from "@/api/skillWorkshop";
import { useTodos } from "@/api/todos";
import { useWorkflowRuns } from "@/api/workflows";
import { appsNeedYouLabel, countMeetingAttention, countPendingSkills, countTodosDue } from "@/features/attention/predicates";
import { attentionTone, type AttentionTone } from "@/features/attention/tones";
import { useRememberedNav } from "@/features/navigation/useNavFlags";
import { useTasksNow } from "@/features/tasks/useTasksNow";
import { jobsWaiting } from "@/features/workflows/jobs/jobModel";
import { joinList, pluralize, todayKey } from "@/lib/format";
import { useAuth } from "@/stores/auth";

const BADGE_POLL = { refetchInterval: 60_000 };

export interface CompanionAttention {
  /** Meetings that wait on the operator (meetingWaitsOnYou): asked about in chat, or a failed recording. */
  meetings: number;
  /** Open todos due today or earlier (`due` includes `overdue`). */
  todos: { due: number; overdue: number; enabled: boolean };
  /** Skills to review: the review queue, plus open workshop proposals when asked for. */
  skills: number;
  /** Tasks waiting for you (an OK, or an answer not delivered); 0 with Tasks off. */
  tasks: number;
  /** Apps that wait on you (down, a yes, a question), where Apps is on (apps/summary). */
  apps?: { waiting: number; down: number };
}

/**
 * The counts the companion shows. A child login gets none of them: the
 * operator's meetings backlog means nothing to a child, and the other
 * endpoints are operator routes.
 */
export function useCompanionAttention(
  customerId: string | null | undefined,
  { workshop = false }: { workshop?: boolean } = {},
): CompanionAttention {
  const isChild = useAuth((s) => s.role) === "family";
  const op = isChild ? null : customerId || null;
  const meetingsQ = useMeetings(op, BADGE_POLL);
  const settingsQ = useCustomerSettings(op);
  // Remembered until the settings answer, as on the desktop (useAttentionCounts).
  const remembered = useRememberedNav(op)?.flags;
  const enabled = settingsQ.data ? settingsQ.data.todos_enabled === true : remembered?.todos_enabled === true;
  const todosQ = useTodos(enabled ? op : null, "today", null, BADGE_POLL);
  const pendingQ = usePendingSkills(op, BADGE_POLL);
  const workshopQ = useWorkshopProposals(workshop ? op : null, { refetchInterval: 120_000 });
  // The same live-tasks list Today and Home read (one key): its server-side count.
  const tasksNow = useTasksNow(op, { poll: BADGE_POLL.refetchInterval });
  // Apps (ui-plan §2.11): the summary, polled like the others, only where the switch is on.
  const appsOn = settingsQ.data ? (settingsQ.data as { apps_enabled?: unknown }).apps_enabled === true : remembered?.apps_enabled === true;
  const appsQ = useAppsSummary(appsOn ? op : null);
  // Team tasks whose plan waits for an OK count with the tasks (Today lists them with Approve)
  const workflowsOn = settingsQ.data ? settingsQ.data.workflows_enabled === true : remembered?.workflows_enabled === true;
  const liveRuns = useWorkflowRuns(workflowsOn ? op : null, { live: true, poll: BADGE_POLL.refetchInterval });

  // Recount when the day turns over: "overdue" depends on today's date.
  const today = todayKey();
  const meetings = useMemo(
    () => countMeetingAttention(meetingsQ.data?.meetings ?? []).waitingOnYou,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meetingsQ.data, today],
  );
  const todos = useMemo(() => {
    const t = countTodosDue(enabled ? todosQ.data?.todos ?? [] : []);
    return { due: t.dueToday + t.overdue, overdue: t.overdue, enabled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todosQ.data, enabled, today]);
  const skills = countPendingSkills(pendingQ.data?.skills ?? [], workshop ? workshopQ.data?.proposals ?? [] : []);
  const apps = appsOn && appsQ.data ? { waiting: appsWaiting(appsQ.data), down: appsQ.data.down } : undefined;
  return { meetings, todos, skills, tasks: (tasksNow.enabled ? tasksNow.needsYou : 0) + jobsWaiting(liveRuns.data?.runs).length, apps };
}

export interface TabBadge {
  /** null = a dot (something waits inside, see the rows). */
  count: number | null;
  /** Read after the item's name: "3 meetings need you". */
  label: string;
  /** The shared attention tone (features/attention/tones): amber only for
   *  overdue todos, grey for a count that only says how many wait. */
  tone: AttentionTone;
}

export type TabBadges = Partial<Record<string, TabBadge>>;

/** Row counts for the More screen, keyed by companion path. */
export function moreRowBadges(a: CompanionAttention): TabBadges {
  const out: TabBadges = {};
  const { due, overdue, enabled } = a.todos;
  if (enabled && due > 0) {
    out["/companion/todos"] = {
      count: due,
      label: overdue > 0 ? `${pluralize(due, "todo")} due (${overdue} overdue)` : `${pluralize(due, "todo")} due today`,
      tone: attentionTone("todos", { overdue }),
    };
  }
  if (a.skills > 0) {
    out["/companion/skills"] = { count: a.skills, label: `${pluralize(a.skills, "skill")} to review`, tone: attentionTone("skills") };
  }
  if (a.apps && a.apps.waiting > 0) {
    // The same count as the Code › Apps tab: what waits on you; red while one is down.
    out["/companion/apps"] = { count: a.apps.waiting, label: appsNeedYouLabel(a.apps.waiting), tone: a.apps.down > 0 ? "danger" : "warning" };
  }
  if (a.tasks > 0) {
    // Tasks is a More row on a phone: its count is the only sign a task waits for your OK (UX review H1, M5).
    out["/companion/tasks"] = { count: a.tasks, label: `${pluralize(a.tasks, "task")} ${a.tasks === 1 ? "needs" : "need"} you`, tone: attentionTone("tasks") };
  }
  return out;
}

/**
 * Tab-bar badges: Meetings shows its count, More a dot when a row inside
 * has one — amber only when a todo is overdue, like the Todos row.
 */
export function useTabBadges(a: CompanionAttention, isChild: boolean): TabBadges {
  const m = a.meetings;
  const rows = moreRowBadges(a);
  const apps = Boolean(rows["/companion/apps"]);
  const waiting = Boolean(rows["/companion/todos"] || rows["/companion/skills"] || rows["/companion/tasks"] || apps);
  const overdue = Boolean(rows["/companion/todos"]) && a.todos.overdue > 0;
  const tasks = Boolean(rows["/companion/tasks"]);
  return useMemo(() => {
    const out: TabBadges = {};
    if (isChild) return out;
    if (m > 0) {
      out["/companion/meetings"] = { count: m, label: `${pluralize(m, "meeting")} ${m === 1 ? "needs" : "need"} you`, tone: attentionTone("meetings") };
    }
    // Worded generically: the shell counts the skills review queue only
    // (the Skills row also counts workshop proposals, fetched only while
    // More is open), so naming what waits could leave something out.
    if (waiting) {
      out["/companion/more"] = {
        count: null,
        // The label names what made the dot amber (overdue todos) first.
        label: waitingLabel(overdue, tasks, apps),
        tone: overdue ? "warning" : "default",
      };
    }
    return out;
  }, [m, waiting, overdue, tasks, apps, isChild]);
}

/** The More dot's spoken words: "items waiting, including overdue todos and tasks that need you". */
export function waitingLabel(overdue: boolean, tasks: boolean, apps = false): string {
  const parts = [overdue && "overdue todos", tasks && "tasks that need you", apps && "apps that need you"].filter((p): p is string => !!p);
  return parts.length ? `items waiting, including ${joinList(parts)}` : "items waiting";
}
